import { ToastBar, Toaster, toast } from 'react-hot-toast'
import { ApiError } from '@/api/httpClient'
import i18n from '@/i18n'
import { usePlanDialogStore } from '@/stores/planDialogStore'
import { shouldOpenPlanDialogForError } from '@/utils/planDialogErrors'

type ToastMessage = Parameters<typeof toast>[0]
type ToastOptions = Parameters<typeof toast>[1]

type MorphToast = typeof toast & {
  warning: (message: ToastMessage, options?: ToastOptions) => string
  fromError: (err: unknown, fallback?: string, options?: ToastOptions) => string
}

function httpStatusTitle(status: number): string {
  return i18n.t(`httpStatus.${status}`, { defaultValue: `HTTP ${status}` })
}

function ApiErrorContent({ title, description }: { title: string; description?: string }) {
  if (!description || description === title) {
    return <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
  }
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
      <span style={{ fontSize: 11, opacity: 0.8 }}>{description}</span>
    </span>
  )
}

const baseToast = ((message: ToastMessage, options?: ToastOptions) =>
  toast(message, { icon: '⚠️', ...options })) as MorphToast

baseToast.success = (message, options) =>
  toast.success(message, { icon: '✅', ...options })

baseToast.error = (message, options) =>
  toast.error(message, { icon: '❌', ...options })

baseToast.warning = (message, options) =>
  toast(message, { icon: '⚠️', ...options })

baseToast.fromError = (err, fallback = 'Erro', options) => {
  if (err instanceof ApiError) {
    if (shouldOpenPlanDialogForError(err.code)) {
      usePlanDialogStore.getState().openDialog()
    }
    const title = httpStatusTitle(err.status)
    const description = err.message && err.message !== String(err.status) ? err.message : undefined
    return toast.error(
      <ApiErrorContent title={title} description={description} />,
      { icon: '❌', ...options },
    )
  }
  if (err instanceof Error) {
    return toast.error(err.message || fallback, { icon: '❌', ...options })
  }
  return toast.error(fallback, { icon: '❌', ...options })
}

baseToast.dismiss = toast.dismiss
baseToast.remove = toast.remove
baseToast.loading = toast.loading
baseToast.custom = toast.custom
baseToast.promise = toast.promise

export const morphToast = baseToast

function toastStyle(type: string): React.CSSProperties {
  const base: React.CSSProperties = { fontSize: '12px' }
  switch (type) {
    case 'success':
      return {
        ...base,
        background: 'var(--color-success-bg)',
        color: 'var(--color-success)',
        border: '1px solid var(--color-success-border)',
      }
    case 'error':
      return {
        ...base,
        background: 'var(--color-danger-bg)',
        color: 'var(--color-danger)',
        border: '1px solid var(--color-danger-border)',
      }
    case 'blank':
      return {
        ...base,
        background: 'var(--color-warning-bg)',
        color: 'var(--color-warning)',
        border: '1px solid var(--color-warning-border)',
      }
    default:
      return {
        ...base,
        background: 'var(--bg-card)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
      }
  }
}

export function ToastViewport() {
  return (
    <Toaster position="top-right">
      {(currentToast) => (
        <div
          onClick={() => toast.dismiss(currentToast.id)}
          style={{ cursor: 'pointer' }}
        >
          <ToastBar toast={currentToast} style={toastStyle(currentToast.type)} />
        </div>
      )}
    </Toaster>
  )
}
